import { Icon, TableProps } from "@amzn/awsui-components-console";
import { CSSProperties } from "react";

import CreateHiResButton from "./CreateHiResButton";
import { EnumeratedIconikAsset, IconikAsset } from "./types";

/** Outlined pill-button colors, matching the media-preview design spec. */
export const ICONIK_HIRES_COLOR = "#4ecdc4";
export const ICONIK_PROXY_COLOR = "#7aa2f7";
export const ICONIK_CART_COLOR = "#ff9800";

/** Shared base style for the outlined pill action buttons in the asset table. */
const PILL_BUTTON_STYLE: CSSProperties = {
  padding: "3px 10px",
  borderRadius: "12px",
  fontSize: "11px",
  lineHeight: "normal",
  border: "1px solid",
  background: "none",
  textDecoration: "none",
  whiteSpace: "nowrap",
  cursor: "pointer",
};

/** Build the style for a colored outlined pill button. */
export const pillButtonStyle = (color: string): CSSProperties => ({
  ...PILL_BUTTON_STYLE,
  color,
  borderColor: color,
});

/**
 * Media-type indicator icon. Same Cloudscape icon for both, differentiated by
 * color: blue (proxy color) for a regular clip, teal (hi-res color) for a
 * user-created sub-clip. Reused as a legend in the per-play counts bar.
 */
export const MediaTypeIcon = ({ isSubClip }: { isSubClip?: boolean }) => {
  const label = isSubClip ? "Sub-Clip" : "Clip";
  return (
    <span
      title={label}
      aria-label={label}
      style={{
        display: "inline-flex",
        color: isSubClip ? ICONIK_HIRES_COLOR : ICONIK_PROXY_COLOR,
      }}
    >
      <Icon name="video-camera-on" />
    </span>
  );
};

/**
 * Left-truncation style for the filename: the full name shows when there's
 * room, and as the column narrows (e.g. when the side panel opens and the chat
 * view shrinks) it truncates from the FRONT, one character at a time, with a
 * leading ellipsis — keeping the distinguishing tail visible
 * (e.g. "Filename0001" -> "…lename0001" -> "…ame0001").
 *
 * ``direction: rtl`` flips the side ``text-overflow: ellipsis`` clips to (the
 * front/left). The filenames are all-LTR (letters, digits, ``_-.``) with no
 * strong RTL characters, so the visible characters keep their normal order.
 */
const FILENAME_TRUNCATE_STYLE: CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  direction: "rtl",
  textAlign: "left",
};

export const iconikTableStaticColumnDefinitions: TableProps["columnDefinitions"] =
  [
    {
      id: "index",
      header: "#",
      // Fits up to 3-digit row numbers plus cell padding; fixed — no resize.
      width: 48,
      minWidth: 48,
      cell: ({ index }: EnumeratedIconikAsset) => index,
      sortingField: "index",
    },
    {
      id: "mediaType",
      // Header label matches what users see — kept narrow (icon + padding).
      header: "",
      width: 52,
      minWidth: 52,
      cell: ({ isSubClip }: IconikAsset) => (
        <MediaTypeIcon isSubClip={isSubClip} />
      ),
    },
    {
      id: "filename",
      header: "File Name",
      // Starting width; the user can drag this column wider or narrower.
      // minWidth allows the column to shrink enough to trigger front-truncation.
      width: 220,
      minWidth: 80,
      cell: ({ assetId, filename }: IconikAsset) => (
        // Filenames link to the asset in Iconik (opens a new tab). The full
        // name is shown and truncates from the front as the column narrows;
        // hovering still reveals the complete filename via the native tooltip.
        <a
          href={`https://app.iconik.io/asset/${assetId}`}
          target="_blank"
          rel="noreferrer"
          title={filename}
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            width: "100%",
            maxWidth: "100%",
          }}
        >
          <span style={FILENAME_TRUNCATE_STYLE}>{filename}</span>
          <Icon name="external" size="small" />
        </a>
      ),
      sortingField: "filename",
    },
    {
      id: "feedType",
      header: "Feed Type",
      // Wide enough for the "Feed Type" header label and a typical pill value
      // like "Melt" or "Scanner"; fixed — no resize.
      width: 115,
      minWidth: 115,
      cell: ({ feedType }: IconikAsset) =>
        feedType ? (
          // Non-clickable colored pill (same treatment as the download pills).
          <span
            style={{
              ...pillButtonStyle(ICONIK_CART_COLOR),
              cursor: "default",
              display: "inline-block",
              whiteSpace: "nowrap",
            }}
          >
            {feedType}
          </span>
        ) : (
          <span style={{ fontSize: "12px", color: "#777", whiteSpace: "nowrap" }}>—</span>
        ),
    },
    {
      id: "download",
      header: "Download",
      // Wide enough for the "Create Hi-Res" pill (shown for sub-clips without
      // a hi-res file) next to the Proxy pill; fixed — no resize.
      width: 200,
      minWidth: 200,
      cell: ({ assetId, downloadUrl, proxyUrl, isSubClip }: IconikAsset) => (
        <span
          // Keep download clicks from also opening the clip preview row.
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "inline-flex",
            gap: "6px",
            whiteSpace: "nowrap",
          }}
        >
          {downloadUrl ? (
            <a href={downloadUrl} download style={pillButtonStyle(ICONIK_HIRES_COLOR)}>
              Hi-Res
            </a>
          ) : (
            // Sub-clips have no hi-res file until an Iconik render is
            // triggered; offer the "Create Hi-Res" action in the Hi-Res slot.
            // Full assets without a hi-res file show nothing here (as before).
            isSubClip && <CreateHiResButton assetId={assetId} />
          )}
          {proxyUrl && (
            <a href={proxyUrl} download style={pillButtonStyle(ICONIK_PROXY_COLOR)}>
              Proxy
            </a>
          )}
        </span>
      ),
    },
  ];
